import React from 'react';
import { HoverTooltip } from 'components/common/HoverTooltip';
import {
  ArrayFieldTemplateProps,
  ArrayFieldTemplateItemType,
} from '@rjsf/utils';
import { useTranslation } from 'react-i18next';
import { ArrowUp, ArrowDown, Delete } from 'components/common/ProjectIcons';
import { buttonClasses } from 'components/common/tailwindClassnames';
import { hasScalarListItems, isBlankListItem } from './blankListItems';

// A quarter smaller than the 30px default: in a list row it sits beside the
// move arrows and should not outweigh them. Shared with CustomArrayField.
export const LIST_DELETE_ICON_SIZE = 22.5;

// Template for rjsf-rendered arrays (arrays of objects — see generateUISchema's
// hasScalarItems gate; scalar arrays go through CustomArrayField instead).
// Lays the title and description full-width above the item list so nested
// arrays keep the whole card width instead of halving it at every level.
const CustomArrayFieldTemplate = (props: ArrayFieldTemplateProps) => {
  const {
    title,
    schema,
    items,
    canAdd,
    onAddClick,
    disabled,
    readonly,
    required,
    formData,
  } = props;
  const { t } = useTranslation();
  const showAdd = canAdd && !readonly;
  // One empty row at a time: another Add would only stack blank rows the
  // user then has to delete (they are dropped on save, but read as errors
  // until then). Reaches plain lists nested in object rows; top-level ones
  // render through CustomArrayField, and reference lists as one multi-select.
  // Lists of objects are left alone — a row there is a set of fields with
  // their own defaults, not a single value.
  const hasBlankRow =
    hasScalarListItems(schema.items) &&
    Array.isArray(formData) &&
    formData.some(isBlankListItem);

  return (
    <div className="my-2 w-full">
      {/* The Add button sits beside the title and description, centred on
                them, the same way CustomArrayField places it for scalar lists. */}
      {(title || schema.description || showAdd) && (
        <div className="mb-2 flex flex-row items-center justify-between gap-6">
          {/* Half the row for title and description, like every other
                        field (HorizontalFieldTemplate's 50/50 split). */}
          <div className="flex w-1/2 min-w-0 flex-col">
            {title && (
              <label className="font-semibold">
                {title}
                {required && (
                  <span className="ml-1 font-normal text-ui-status-error-destructive">
                    *
                  </span>
                )}
              </label>
            )}
            {schema.description && (
              <span className="mt-1 text-sm text-ui-text-primary-text">
                {schema.description}
              </span>
            )}
          </div>
          {showAdd && (
            <div className="flex w-1/2 min-w-0 justify-end">
              <button
                type="button"
                className={`${buttonClasses.listAdd} shrink-0`}
                disabled={disabled || hasBlankRow}
                title={
                  hasBlankRow
                    ? t('Fill in or remove the empty row first.')
                    : undefined
                }
                onClick={onAddClick}
                data-testid="CustomArrayFieldTemplate-add-button"
              >
                {t('Add Item')}
              </button>
            </div>
          )}
        </div>
      )}
      {items.map(
        ({
          key,
          index,
          children,
          hasToolbar,
          hasMoveUp,
          hasMoveDown,
          hasRemove,
          onReorderClick,
          onDropIndexClick,
        }: ArrayFieldTemplateItemType) => (
          <div
            key={key}
            className="mb-3 flex w-full flex-row items-center gap-2 transition-all duration-300"
            data-testid={`CustomArrayFieldTemplate-item-${index}`}
          >
            <div className="min-w-0 flex-1">{children}</div>
            {/* A flex sibling, not an absolute overlay on the fields, centred
                        on the item like every other control in these forms. Items
                        carry no border of their own: the list's divider frames them. */}
            {hasToolbar && !readonly && (
              <div className="flex shrink-0 flex-row items-center gap-1">
                {(hasMoveUp || hasMoveDown) && (
                  <>
                    <HoverTooltip
                      content={t('Move up') || 'Move up'}
                      position="top"
                      focusable={false}
                    >
                      <button
                        type="button"
                        className="rounded p-1 hover:bg-greyscale-10 disabled:cursor-not-allowed disabled:opacity-30"
                        aria-label={t('Move up') || 'Move up'}
                        disabled={disabled || !hasMoveUp}
                        onClick={onReorderClick(index, index - 1)}
                      >
                        <ArrowUp />
                      </button>
                    </HoverTooltip>
                    <HoverTooltip
                      content={t('Move down') || 'Move down'}
                      position="top"
                      focusable={false}
                    >
                      <button
                        type="button"
                        className="rounded p-1 hover:bg-greyscale-10 disabled:cursor-not-allowed disabled:opacity-30"
                        aria-label={t('Move down') || 'Move down'}
                        disabled={disabled || !hasMoveDown}
                        onClick={onReorderClick(index, index + 1)}
                      >
                        <ArrowDown />
                      </button>
                    </HoverTooltip>
                  </>
                )}
                {hasRemove && (
                  <HoverTooltip
                    content={t('Remove') || 'Remove'}
                    position="top"
                    focusable={false}
                  >
                    <button
                      type="button"
                      className="rounded p-1 hover:bg-greyscale-10 disabled:cursor-not-allowed disabled:opacity-30"
                      aria-label={t('Remove') || 'Remove'}
                      disabled={disabled}
                      onClick={onDropIndexClick(index)}
                    >
                      <Delete size={LIST_DELETE_ICON_SIZE} />
                    </button>
                  </HoverTooltip>
                )}
              </div>
            )}
          </div>
        ),
      )}
    </div>
  );
};

export default CustomArrayFieldTemplate;
